import type { AggregatesArtifact, DashboardHandoff } from '../../data/types';
import { useBeatOrchestration } from '../../motion/useBeatOrchestration';

export interface ReportEssayDefinition {
  id: string;
  afterChapter: 1 | 3 | 5;
  marker: string;
  eyebrow: string;
  title: string;
  paragraphs: readonly string[];
  sourceBadges: readonly string[];
  dashboardTab: DashboardHandoff['tab'];
  destination: 'dashboard' | 'voices';
  actionLabel: string;
}

export const REPORT_ESSAYS: readonly ReportEssayDefinition[] = [
  {
    id: 'essay-event-day-truth',
    afterChapter: 1,
    marker: 'READING PAUSE 01',
    eyebrow: 'この調査が、正確に語れること',
    title: '1,386人の声は、ATCの全来場者像ではない。だからこそ、当日の現実には強い。',
    paragraphs: [
      'この調査は、夏の抽選会・イベント期間の九日間に、会場で回答を得たものである。家族連れ、イベントを目的に訪れた人、来館頻度の低い人が多い。これは偏りとして隠すべき条件ではなく、数字を正しく読むための輪郭だ。',
      '見えているのは「ATCに来る可能性のあるすべての人」ではない。イベントが実際に動かした人、その日に館内で何を感じたか、という現場の記録である。以降の章では、この強みと限界を同時に置いたまま数字を読む。',
    ],
    sourceBadges: ['選択式回答', '調査方法'],
    dashboardTab: 'audience',
    destination: 'dashboard',
    actionLabel: '来場者の構成を確かめる',
  },
  {
    id: 'essay-satisfaction-is-not-understanding',
    afterChapter: 3,
    marker: 'READING PAUSE 02',
    eyebrow: '二つの数字を、同じ画面で読む',
    title: '「楽しかった」は入口であって、「ATCを知った」と同じではない。',
    paragraphs: [
      '当日の満足は高い。それでも、主要施設を一つも知らない人が876人いた。さらに736人は、ATCにオフィスがあることをこの調査で初めて知った。好意的な体験と、ATC全体への理解は、同時には育っていない。',
      'これは「イベントが失敗した」という話ではない。むしろ、イベントで生まれた満足を、その日のうちに館内回遊や次の来館理由へ接続できる余地が大きい、という測定結果である。88.4%の成功を、63.2%の未認知にどう渡すか。ここからが経営課題になる。',
    ],
    sourceBadges: ['選択式回答', '測定された対比'],
    dashboardTab: 'awareness',
    destination: 'dashboard',
    actionLabel: '満足と認知を比較する',
  },
  {
    id: 'essay-memory-is-brand',
    afterChapter: 5,
    marker: 'READING PAUSE 03',
    eyebrow: '数字の外側に残る、ATCの資産',
    title: '場所のブランドは、ロゴだけではなく、人生の途中で呼び戻される記憶に宿る。',
    paragraphs: [
      'フェリーとバイクの旅、三世代で訪れた時間、海を眺めながら夫婦で食べた昼食。回答は、施設名の認知だけでは捉えられないATCとの関係を伝えている。海、旅、家族、イベントは、別々の話題ではなく、場所を思い出すための入口になっている。',
      'エピソードを尋ねた機会は111件に限られ、有効な記述は93件、長文は8件だった。全来場者を代表する量ではない。それでも、長い時間を越えて語られた記憶は、ATCがすでに持つブランド資産の存在を示す。次に必要なのは、偶然残った記憶を、次の来館理由へつなぐ設計である。',
    ],
    sourceBadges: ['自由記述の分類', '編集部による統合'],
    dashboardTab: 'needs',
    destination: 'voices',
    actionLabel: '記憶の声を読む',
  },
] as const;

function EssayEvidence({
  afterChapter,
  story,
}: {
  afterChapter: ReportEssayDefinition['afterChapter'];
  story: AggregatesArtifact['story'];
}) {
  const h = story.headlines;
  if (afterChapter === 1) {
    return <div className="grid gap-3 sm:grid-cols-3">
      {[
        ['回答者', '1,386人', '九日間の会場調査'],
        ['イベント目的', `${h.eventPurpose.n}人`, `${h.eventPurpose.rate}%`],
        ['家族と来場', `${h.family.n}人`, `${h.family.rate}%`],
      ].map(([label, value, note]) => <div data-reveal-item key={label} className="rounded-2xl border border-white/15 bg-white/8 p-4">
        <p className="text-xs font-bold text-white/60">{label}</p>
        <strong className="mt-2 block text-3xl tabular-nums">{value}</strong>
        <p className="mt-1 text-xs text-white/65">{note}</p>
      </div>)}
    </div>;
  }
  if (afterChapter === 3) {
    return <div className="grid gap-3 sm:grid-cols-[1fr_auto_1fr] sm:items-stretch">
      <div data-reveal-item className="rounded-2xl bg-white p-5 text-atc-navy">
        <p className="text-xs font-bold text-atc-slate">当日の満足</p>
        <strong className="mt-2 block text-4xl tabular-nums text-atc-primary">{h.positiveSatisfaction.rate}%</strong>
        <p className="mt-2 text-xs">{h.positiveSatisfaction.n.toLocaleString()}人 / 1,386人</p>
      </div>
      <span data-reveal-item className="grid place-items-center px-2 text-2xl font-bold text-white/55" aria-hidden="true">≠</span>
      <div data-reveal-item className="rounded-2xl border border-atc-navy/10 bg-white p-5 text-atc-navy">
        <p className="text-xs font-bold text-atc-slate">主要施設を一つも知らない</p>
        <strong className="mt-2 block text-4xl tabular-nums">{h.facilityNone.rate}%</strong>
        <p className="mt-2 text-xs">{h.facilityNone.n.toLocaleString()}人 / 1,386人</p>
      </div>
    </div>;
  }
  return <div className="grid gap-3 sm:grid-cols-3">
    {[
      ['質問機会', story.episodes.askedN, '件'],
      ['有効エピソード', story.episodes.usableN, '件'],
      ['長文', story.episodes.longN, '件'],
    ].map(([label, value, unit]) => <div data-reveal-item key={label} className="rounded-2xl border border-white/15 bg-white/8 p-4">
      <p className="text-xs font-bold text-white/60">{label}</p>
      <strong className="mt-2 block text-3xl tabular-nums">{value}<span className="ml-1 text-sm">{unit}</span></strong>
    </div>)}
  </div>;
}

export function ReportEssayInterlude({
  definition,
  aggregates,
  onHandoff,
}: {
  definition: ReportEssayDefinition;
  aggregates: AggregatesArtifact;
  onHandoff(tab: DashboardHandoff['tab'], destination: 'dashboard' | 'voices'): void;
}) {
  const motionRoot = useBeatOrchestration({ active: true });
  return <aside
    id={definition.id}
    className="relative z-10 grid min-h-[88svh] content-center overflow-hidden bg-atc-navy px-6 py-24 text-white md:px-12 lg:px-20"
    aria-labelledby={`${definition.id}-heading`}
  >
    <div className="pointer-events-none absolute -right-24 top-1/2 h-80 w-80 -translate-y-1/2 rounded-full border border-white/10" aria-hidden="true" />
    <div className="pointer-events-none absolute -right-8 top-1/2 h-52 w-52 -translate-y-1/2 rounded-full border border-white/10" aria-hidden="true" />
    <div ref={motionRoot} className="relative mx-auto grid w-full max-w-[84rem] gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(20rem,.85fr)] lg:items-center">
      <div>
        <div data-section-transition className="mb-7 h-px w-full origin-left bg-gradient-to-r from-white/75 to-transparent" aria-hidden="true" />
        <p data-beat-kicker className="text-xs font-bold tracking-[.2em] text-white/55">{definition.marker}</p>
        <p data-beat-dots className="mt-5 text-sm font-bold tracking-[.08em] text-white/70">{definition.eyebrow}</p>
        <h2 data-beat-question id={`${definition.id}-heading`} className="mt-4 max-w-4xl text-3xl font-bold leading-[1.35] tracking-[-.04em] md:text-5xl">{definition.title}</h2>
        <div data-beat-visual className="mt-8 space-y-5 text-sm font-medium leading-[2] text-white/78 md:text-base">
          {definition.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
        </div>
      </div>
      <div>
        <EssayEvidence afterChapter={definition.afterChapter} story={aggregates.story} />
        <div data-beat-conclusion className="mt-6 rounded-3xl border border-white/15 bg-white/8 p-5 backdrop-blur-sm">
          <div className="flex flex-wrap gap-2">
            {definition.sourceBadges.map((badge) => <span key={badge} className="rounded-full border border-white/15 px-3 py-1 text-[11px] font-bold text-white/75">{badge}</span>)}
          </div>
          <p className="mt-4 text-xs leading-relaxed text-white/60">数値は匿名化済み回答データから生成。解釈は測定値と編集上の統合を区別して記載しています。</p>
          <button
            type="button"
            onClick={() => onHandoff(definition.dashboardTab, definition.destination)}
            className="mt-5 w-full rounded-full bg-white px-5 py-3 text-sm font-bold text-atc-navy transition hover:bg-atc-surface focus-visible:outline focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-atc-accent"
          >
            {definition.actionLabel}
          </button>
        </div>
      </div>
    </div>
  </aside>;
}
